import {z} from 'zod';
import {ApiDefinition} from '../definitions';
import {IntegrationTypeSchema} from '../../integrations/common';
import {ObjectIdSchema} from '../../zod';
import {
  CrmIntegrationTypeSchema,
  HubSpotIntegrationSchema,
  IntegrationConfigSchema,
  SalesforceIntegrationSchema,
} from '../../integrations/crm';
import {LinkedInIntegrationSchema} from '../../integrations/linkedin';
import {zodTypeguard} from '../helpers';
import {SlackIntegrationSchema} from '../../integrations/slack';
import {WebIntentIntegrationSchema} from '../../integrations/webIntent';

const BaseOAuthPayloadSchema = z.object({
  code: z.string(),
  integrationName: IntegrationTypeSchema,
});
const SalesforceOAuthPayloadSchema = BaseOAuthPayloadSchema.extend({
  integrationName: z.literal('salesforce'),
  isSandbox: z.boolean().optional(),
});
const HubSpotOAuthPayloadSchema = BaseOAuthPayloadSchema.extend({
  integrationName: z.literal('hubspot'),
});
const LinkedInOAuthPayloadSchema = BaseOAuthPayloadSchema.extend({
  integrationName: z.literal('linkedin'),
  redirectUri: z.string(),
  context: z.enum(['onboarding', 'integrations']),
});
const SlackOAuthPayloadSchema = BaseOAuthPayloadSchema.extend({
  integrationName: z.literal('slack'),
  redirectUri: z.string(),
});
export const OAuthPayloadSchema = z.union([
  SalesforceOAuthPayloadSchema,
  HubSpotOAuthPayloadSchema,
  LinkedInOAuthPayloadSchema,
  SlackOAuthPayloadSchema,
]);
export type OAuthPayload = z.infer<typeof OAuthPayloadSchema>;

export const AuthIntegration = {
  path: 'integration/auth',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: OAuthPayloadSchema,
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const DisconnectIntegration = {
  path: 'integration/disconnect',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: CrmIntegrationTypeSchema,
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const DeleteIntegration = {
  path: 'integration/delete',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: IntegrationTypeSchema,
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const GetSalesforceErrors = {
  path: 'integration/salesforceErrors',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    taskId: ObjectIdSchema,
  }),
  responseDataSchema: z.string(),
} satisfies ApiDefinition;

const integrationOmittedFields = {oauth: true} as const;
const CrmIntegrationResponseSchema = z.union([
  HubSpotIntegrationSchema.omit(integrationOmittedFields),
  SalesforceIntegrationSchema.omit(integrationOmittedFields),
]);

export type CrmIntegrationResponse = z.infer<
  typeof CrmIntegrationResponseSchema
>;
export const isCrmIntegrationResponse = zodTypeguard(
  CrmIntegrationResponseSchema
);

const LinkedInIntegrationResponseSchema = LinkedInIntegrationSchema.omit(
  integrationOmittedFields
);
export type LinkedInIntegrationResponse = z.infer<
  typeof LinkedInIntegrationResponseSchema
>;
export const isLinkedInIntegrationResponse = zodTypeguard(
  LinkedInIntegrationResponseSchema
);

const SlackIntegrationResponseSchema = SlackIntegrationSchema.omit(
  integrationOmittedFields
);
export type SlackIntegrationResponse = z.infer<
  typeof SlackIntegrationResponseSchema
>;

const IntegrationResponseSchema = z.union([
  CrmIntegrationResponseSchema,
  LinkedInIntegrationResponseSchema,
  SlackIntegrationResponseSchema,
  WebIntentIntegrationSchema,
]);
export type IntegrationResponse = z.infer<typeof IntegrationResponseSchema>;

export const GetIntegration = {
  path: 'integration/getIntegration',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({
    type: IntegrationTypeSchema,
  }),
  responseDataSchema: IntegrationResponseSchema,
} satisfies ApiDefinition;

export const GetIntegrations = {
  path: 'integration/getIntegrations',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({}),
  responseDataSchema: IntegrationResponseSchema.array(),
} satisfies ApiDefinition;

export const GetLinkedInAdAccounts = {
  path: 'integration/getLinkedInAdAccounts',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({}),
  responseDataSchema: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    })
  ),
} satisfies ApiDefinition;

export const QueueImportLinkedInAdAccounts = {
  path: 'integration/queueImportLinkedInAdAccounts',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({}),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const UpdateIntegration = {
  path: 'integration/updateIntegration',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('crm'),
      integrationType: CrmIntegrationTypeSchema,
      config: IntegrationConfigSchema.partial(),
    }),
    z.object({
      type: z.literal('linkedin'),
      adAccountId: z.number(),
    }),
  ]),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const EnableIntegration = {
  path: 'integration/enableIntegration',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    type: z.enum(['webIntent']),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const CheckWebIntentScriptDetection = {
  path: 'integration/checkWebIntentScriptDetection',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({}),
  responseDataSchema: z.object({
    detectedAt: z.date().optional(),
  }),
} satisfies ApiDefinition;
